/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/no-array-index-key */
import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  Typography,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  Container,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Field, Form, Formik } from 'formik';
import { TextField, TextFieldProps, TextField as TextInput } from 'formik-mui';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useListPaymentMethodsByUserIdQuery } from 'renderer/features/settings/services/paymentMethods.endpoints';
import {
  useListProductsQuery,
  useLazyGetProductDetailsQuery,
} from 'renderer/features/products-page/services/products.endpoints';
// import { formatQueryError } from 'renderer/shared/formatters/formatters';
// import { toValidator } from 'renderer/shared/schemas/common.schemas';
import { JSX } from 'react/jsx-runtime';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { dropdownKeyEventHandler } from 'renderer/shared/callbacks/keyEventHandler';
import OrderSummaryAdmin from 'renderer/features/purchase-page/components/OrderSummaryAdmin';
import DiscountCode from 'renderer/features/purchase-page/components/DiscountCode';
import { useCreateSubscriptionMutation } from '../services/users.endpoints';
// import { CreateSubscriptionSchema } from '../schemas/createSubscription.schema';

interface PaymentMethod {
  _id: string;
  cardType: string;
  last4: string;
  stripeId?: string;
}

const initialForm = {
  productId: '',
  platform: '',
  hostApplication: {
    id: '',
    version: '',
    displayName: '',
  },
  numberOfCopies: 1,
  discountCode: '',
  startDate: dayjs(),
  paymentMethodId: '',
  overrideStripePriceId: '',
};

const formatQueryError = (
  error: FetchBaseQueryError | SerializedError,
): string | React.ReactNode => {
  const { data } = error as any;

  if (typeof data === 'object' && data !== null) {
    if (Array.isArray(data.details)) {
      // Format each validation error
      return (
        <ul>
          {data.details.map((detail: any, index: number) => (
            <li key={`${index}-id`}>{detail.message}</li>
          ))}
        </ul>
      );
    }

    if ('message' in data) {
      return data.message;
    }
  }

  if ('status' in error) {
    if ('data' in error) {
      return `${error.status}: ${error.data}`;
    }
    return error.status;
  }

  return `An unexpected error occurred: ${error}`;
};

function CreateSubscription() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const { data: products } = useListProductsQuery({ includeHidden: true });

  const [
    createSubscription,
    { isLoading: createLoading, isSuccess, error: createError },
  ] = useCreateSubscriptionMutation();
  const subscriptionProducts = products?.filter(
    (p) => p.category === 'Subscription',
  );

  const { data: paymentMethods } = useListPaymentMethodsByUserIdQuery(userId);

  const [getProductDetails, { data: productDetails }] =
    useLazyGetProductDetailsQuery();

  const handleSubmit = async (values: any) => {
    const data = {
      ...values,
      startDate: values.startDate ? values.startDate.toISOString() : null,
      userId,
    };
    console.log(data);
    await createSubscription(data);
  };

  // Redirect if the submission was successful
  useEffect(() => {
    if (isSuccess) {
      navigate(`/users/${userId}`);
    }
  }, [isSuccess, navigate, userId]);

  return (
    <Container maxWidth="sm" sx={{ pb: 20 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <MuiLink underline="hover" color="inherit" href="/">
          Home
        </MuiLink>
        <MuiLink underline="hover" color="inherit" href="/#/users">
          Users
        </MuiLink>
        <MuiLink underline="hover" color="inherit" href={`/#/users/${userId}`}>
          User Details
        </MuiLink>
        <Typography color="text.primary">Create Subscription</Typography>
      </Breadcrumbs>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
                  Create Subscription
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Formik
                initialValues={initialForm}
                // validate={(values) => {
                //   console.log('values', values);
                //   const formSchema = CreateSubscriptionSchema;
                //   return toValidator(formSchema)(values);
                // }}
                onSubmit={handleSubmit}
              >
                {({ values, handleChange, setFieldValue }) => (
                  <Form>
                    {createError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {formatQueryError(createError)}
                      </Alert>
                    )}

                    <List>
                      <ListItem>
                        <Field
                          component={TextInput}
                          onChange={(
                            e: React.ChangeEvent<HTMLTextAreaElement>,
                          ) => {
                            getProductDetails(e.target.value);
                            handleChange(e);
                          }}
                          onKeyUp={dropdownKeyEventHandler(
                            subscriptionProducts?.map((product) => ({
                              label: product.name,
                              value: product._id,
                            })) ?? [],
                            (option) => {
                              setFieldValue('productId', option);
                            },
                          )}
                          fullWidth
                          select
                          size="small"
                          id="productId"
                          name="productId"
                          label="Product"
                        >
                          {subscriptionProducts?.map(({ name, _id }) => (
                            <MenuItem key={`${_id}-${name}`} value={_id}>
                              {name}
                            </MenuItem>
                          ))}
                        </Field>
                      </ListItem>

                      {/* <ListItem>
                        <Field
                          component={TextInput}
                          id="platform"
                          label="Platform"
                          name="platform"
                          size="small"
                          fullWidth
                          select
                          value={values.platform}
                          onChange={handleChange}
                          onKeyUp={dropdownKeyEventHandler(
                            productDetails?.supportedPlatforms.map(
                              (platform) => ({
                                label: platform,
                                value: platform,
                              }),
                            ) ?? [],
                            (option) => setFieldValue('platform', option),
                          )}
                        >
                          {productDetails?.supportedPlatforms.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Field>
                      </ListItem> */}

                      {/* <ListItem>
                        <Field
                          component={TextInput}
                          id="hostApplicationId"
                          label="Host Application"
                          name="hostApplicationId"
                          size="small"
                          fullWidth
                          select
                          value={values.hostApplicationId}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            if (products) {
                              const [fullProduct] = products.filter(
                                (p) => p._id === values.productId,
                              );
                              const { supportedHostApplications } = fullProduct;
                              const [hostApp] =
                                supportedHostApplications.filter(
                                  (h) => h.id === e.target.value,
                                );
                              setFieldValue('hostApplication', hostApp);
                              handleChange(e);
                            }
                          }}
                        >
                          {productDetails?.supportedHostApplications.map(
                            ({ displayName, id }) => (
                              <MenuItem key={id} value={id}>
                                {displayName}
                              </MenuItem>
                            ),
                          )}
                        </Field>
                      </ListItem> */}

                      <ListItem>
                        <Field
                          component={DatePicker}
                          label="Start Date"
                          name="startDate"
                          value={values.startDate}
                          onChange={(newValue: any) =>
                            // setFieldValue(
                            //   'startDate',
                            //   newValue ? new Date(newValue).toISOString() : '',
                            // )
                            setFieldValue('startDate', newValue)
                          }
                          renderInput={(
                            params: JSX.IntrinsicAttributes & TextFieldProps,
                          ) => <TextInput {...params} fullWidth />}
                        />
                      </ListItem>

                      <ListItem>
                        <Field
                          component={TextInput}
                          id="paymentMethodId"
                          label="Payment Method"
                          name="paymentMethodId"
                          size="small"
                          fullWidth
                          select
                          value={values.paymentMethodId}
                          onChange={handleChange}
                        >
                          {paymentMethods
                            ?.filter(
                              (paymentMethod: any) =>
                                paymentMethod.stripeId !== undefined,
                            )
                            .map(({ _id, cardType, last4 }: PaymentMethod) => (
                              <MenuItem key={_id} value={_id}>
                                {`${cardType} ending in ${last4}`}
                              </MenuItem>
                            ))}
                        </Field>
                      </ListItem>

                      <ListItem>
                        <Field
                          component={TextField}
                          label="Override Stripe Price ID"
                          helperText="Paste in a Stripe Price ID to charge the customer a different price"
                          placeholder="price_abcdefghijklmnopqrstuvwxyz"
                          name="overrideStripePriceId"
                          id="overrideStripePriceId"
                          size="small"
                          fullWidth
                        />
                      </ListItem>

                      <ListItem>
                        <OrderSummaryAdmin
                          product={productDetails}
                          disableCopies
                        />
                      </ListItem>
                      <ListItem>
                        <DiscountCode
                          mode="admin"
                          onDiscountUpdate={(result) => {
                            setFieldValue(
                              'discountCode',
                              result?.discount?.code ?? '',
                            );
                          }}
                        />
                      </ListItem>

                      <ListItem>
                        <Button
                          variant="contained"
                          color="primary"
                          type="submit"
                          disabled={createLoading}
                        >
                          {createLoading ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            'Create Subscription'
                          )}
                        </Button>
                      </ListItem>
                    </List>
                  </Form>
                )}
              </Formik>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default CreateSubscription;
